import { transition, trigger } from '@angular/animations';
import { CdkScrollable } from '@angular/cdk/overlay';
import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { ButtonModule, DIALOG_DATA, DialogRef, IconModule } from '@hglobal/common';
import { TranslateModule } from '@ngx-translate/core';

import { DialogData } from '@pp/core/entities';

import { left, right } from './base-dialog.animations';

const FIRST_COMPONENT = 0;
const STEP = 1;

@Component({
  selector: 'pp-base-dialog',
  imports: [IconModule, ButtonModule, NgComponentOutlet, TranslateModule, CdkScrollable],
  templateUrl: './base-dialog.component.html',
  styleUrl: './base-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [trigger('slider', [transition(':increment', right), transition(':decrement', left)])],
})
export class BaseDialogComponent<T, V> {
  dialogRef = inject(DialogRef);
  data = inject<DialogData<T, V>>(DIALOG_DATA);

  index = FIRST_COMPONENT;

  get component() {
    return this.data.components[this.index];
  }

  get count(): number {
    return this.data.components.length;
  }

  get title(): string[] | string {
    const title: string | string[] = this.data.title;

    if (Array.isArray(title)) {
      return title.slice(0, this.index + 1).join(' / ');
    }

    return title;
  }

  get isFirstStep(): boolean {
    return this.index === FIRST_COMPONENT;
  }

  get isLastStep(): boolean {
    return this.index === this.count - 1;
  }

  next(currentStepData?: any): void {
    if (this.isLastStep) {
      delete this.data['prevComponentData'];

      return;
    }

    if (currentStepData) {
      this.data.prevComponentData = currentStepData;
    } else if (!currentStepData && this.data.prevComponentData) {
      delete this.data['prevComponentData'];
    }

    this.index += STEP;
  }

  prev(): void {
    if (this.data.prevComponentData) {
      delete this.data['prevComponentData'];
    }

    if (this.isFirstStep) {
      return;
    }

    this.index -= STEP;
  }

  moveTo(i: number, currentStepData?: T): void {
    if (i > this.count - 1) {
      return;
    }

    if (currentStepData) {
      this.data.prevComponentData = currentStepData;
    } else if (!currentStepData && this.data.prevComponentData) {
      delete this.data['prevComponentData'];
    }

    this.index = i;
  }

  close(): void {
    this.dialogRef.close();
  }
}
